import {
  useState,
  useMemo,
  useRef,
  type DragEvent,
  type CSSProperties,
} from 'react';
import { useTranslation } from 'react-i18next';
import CustomSVGs from '../customSVGs/CustomSVGs';
import {
  copySVG,
  download2SVG,
  filledCheckboxSVG,
} from '../../lib/allCustomSVGs';
import '../../styles/listItems/reportsListItem.scss';
import get_base64_string_api_call from '../../lib/api/Reports/get_base64_string_api_call';

const INVALID_FILENAME_CHARS_REGEX = /[<>:"/\\|?*]+/g;

const stripControlCharacters = (value: string) =>
  Array.from(value)
    .filter((char) => char.charCodeAt(0) >= 32)
    .join('');

type DownloadOptions = {
  filename?: string; // ignored for naming; we always use {GraphName}-Report
  width?: number;
  height?: number;
};

type Props = {
  id: string;
  icon: any;
  iconColor?: string;
  title: string; // <-- used to build {GraphName}-Report
  subTitle: string;
  tag?: string;
  shortCode?: string | null;
  syncStatusLabel?: string;
  syncStatusIsSynced?: boolean;
  onConfigClick: () => void;
  configIcon?: any;
  configIconColor?: string;
  configLoading?: boolean;
  onEditClick?: () => void;
  editIcon?: any;
  editIconColor?: string;
  editLoading?: boolean;
  editIconSize?: string;
  base64String?: string | null; // data URL or raw base64
  downloadOptions?: DownloadOptions; // width/height honored; filename ignored
  dragIcon?: any;
  dragIconColor?: string;
  dragLoading?: boolean;
  isDragDisabled?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (event: DragEvent<HTMLDivElement>) => void;
  onDragEnter?: (event: DragEvent<HTMLDivElement>) => void;
  onDragOver?: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (event: DragEvent<HTMLDivElement>) => void;
  onDrop?: (event: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (event: DragEvent<HTMLDivElement>) => void;
};

type HoverKey = `${string}:${'copy' | 'download' | 'drag'}`;

const ReportsListItem = ({
  id,
  icon,
  iconColor,
  title,
  subTitle,
  onConfigClick,
  tag,
  shortCode,
  syncStatusLabel,
  syncStatusIsSynced,
  configIconColor,
  configIcon,
  configLoading = false,
  onEditClick,
  editIcon,
  editIconColor,
  editLoading = false,
  editIconSize = '1.8rem',
  base64String,
  downloadOptions,
  dragIcon,
  dragIconColor,
  dragLoading = false,
  isDragDisabled = false,
  isDragging = false,
  isDragOver = false,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: Props) => {
  const { t } = useTranslation();

  // ---- Tooltip & feedback states ----
  const [hoverKey, setHoverKey] = useState<HoverKey | null>(null);
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [downloadedId, setDownloadedId] = useState<string | null>(null);

  // ---- Download flow states ----
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [fetchedDataUrl, setFetchedDataUrl] = useState<string | null>(null);
  const dragHandleRef = useRef<HTMLDivElement | null>(null);

  // Normalize any pre-supplied base64 to a data URL
  const normalizedPropDataUrl = useMemo(() => {
    if (!base64String) return null;
    if (base64String.startsWith('data:image/')) return base64String;
    return `data:image/png;base64,${base64String}`;
  }, [base64String]);

  // Prefer fetched data URL, fall back to prop value if present
  const effectiveDataUrl = fetchedDataUrl || normalizedPropDataUrl;

  // Build a safe base filename from the graph name: {GraphName}-Report
  const baseNameFromTitle = useMemo(() => {
    const raw = (title || 'Report').trim();
    const withoutIllegal = raw.replace(INVALID_FILENAME_CHARS_REGEX, '');
    const noControl = stripControlCharacters(withoutIllegal);
    const dashed = noControl.replace(/\s+/g, '-');
    return (dashed || 'Report') + '-Report';
  }, [title]);

  // Decide extension based on mime in data URL (fallback to png)
  const preferredFilename = useMemo(() => {
    const m = effectiveDataUrl?.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/);
    const ext =
      m?.[1]?.toLowerCase().includes('jpeg') ||
      m?.[1]?.toLowerCase().includes('jpg')
        ? 'jpg'
        : m?.[1]?.toLowerCase().includes('webp')
        ? 'webp'
        : m?.[1]?.toLowerCase().includes('gif')
        ? 'gif'
        : 'png';
    return `${baseNameFromTitle}.${ext}`;
  }, [effectiveDataUrl, baseNameFromTitle]);

  const clearTimer = () => {
    if (tooltipTimer) {
      clearTimeout(tooltipTimer);
      setTooltipTimer(null);
    }
  };

  const handleMouseEnter = (key: HoverKey) => {
    clearTimer();
    const timer = setTimeout(() => setHoverKey(key), 300);
    setTooltipTimer(timer);
  };

  const handleMouseLeave = () => {
    setHoverKey(null);
    clearTimer();
  };

  // ---- Copy Shortcode ----
  const copyToClipboard = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(itemId);
      setTimeout(() => setCopiedId(null), 900);
    } catch {
      console.error('Copy failed');
    }
  };

  // ---- Helpers ----
  const coerceToDataUrl = (maybe: any): string | null => {
    if (!maybe) return null;

    if (typeof maybe === 'string') {
      if (maybe.startsWith('data:image/')) return maybe;
      return `data:image/png;base64,${maybe}`;
    }

    if (maybe.base64_image && typeof maybe.base64_image === 'string') {
      return maybe.base64_image.startsWith('data:image/')
        ? maybe.base64_image
        : `data:image/png;base64,${maybe.base64_image}`;
    }
    if (
      maybe.graph_image?.base64_image &&
      typeof maybe.graph_image.base64_image === 'string'
    ) {
      const s = maybe.graph_image.base64_image;
      return s.startsWith('data:image/') ? s : `data:image/png;base64,${s}`;
    }

    return null;
  };

  // ---- Download Image from data URL with optional resize ----
  const downloadImageFromDataUrl = async (
    dataUrl: string,
    opts?: DownloadOptions
  ) => {
    // We ALWAYS use {GraphName}-Report for the filename
    const { width, height } = opts || {};
    const finalFilename = preferredFilename;

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = dataUrl;
    });

    let targetW = width;
    let targetH = height;

    if (width && !height) {
      const scale = width / img.width;
      targetH = Math.round(img.height * scale);
    } else if (!width && height) {
      const scale = height / img.height;
      targetW = Math.round(img.width * scale);
    } else if (!width && !height) {
      targetW = img.width;
      targetH = img.height;
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetW!;
    canvas.height = targetH!;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');

    ctx.drawImage(img, 0, 0, targetW!, targetH!);

    const mimeMatch = dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';

    const blob: Blob = await new Promise((resolve) => {
      if (canvas.toBlob) {
        canvas.toBlob((b) => resolve(b as Blob), mime);
      } else {
        const fallbackDataUrl = canvas.toDataURL(mime);
        const arr = fallbackDataUrl.split(',');
        const bstr = atob(arr[1]);
        const n = bstr.length;
        const u8arr = new Uint8Array(n);
        for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
        resolve(new Blob([u8arr], { type: mime }));
      }
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', finalFilename);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadClick = async () => {
    try {
      setDownloadLoading(true);

      let dataUrl = effectiveDataUrl;
      if (!dataUrl) {
        const apiRes = await get_base64_string_api_call(id);
        dataUrl = coerceToDataUrl(apiRes);
        if (dataUrl) setFetchedDataUrl(dataUrl);
      }

      if (dataUrl) {
        await downloadImageFromDataUrl(dataUrl, downloadOptions);
        setDownloadedId(id);
        setTimeout(() => setDownloadedId(null), 900);
      } else {
        console.error('No image data returned for download');
      }
    } catch (e) {
      console.error('Download failed', e);
    } finally {
      setDownloadLoading(false);
    }
  };

  const showCopy = Boolean(shortCode);
  const showDownload = Boolean(downloadOptions);
  const showSyncStatus = Boolean(syncStatusLabel);

  const dragVisualStyle = useMemo<CSSProperties>(() => {
    const style: CSSProperties = {};
    if (isDragging) {
      style.opacity = 0.8;
      style.cursor = 'grabbing';
    }
    if (isDragOver) {
      style.boxShadow = '0 0 0 2px #5573f7 inset';
      style.borderRadius = '1rem';
    }
    return style;
  }, [isDragging, isDragOver]);

  const attachHandler = (
    handler?: (event: DragEvent<HTMLDivElement>) => void
  ) => {
    if (!handler) return undefined;
    return (event: DragEvent<HTMLDivElement>) => handler(event);
  };

  return (
    <div
      className='reportsListItem'
      key={id}
      style={dragVisualStyle}
      onDragEnter={attachHandler(onDragEnter)}
      onDragOver={attachHandler(onDragOver)}
      onDragLeave={attachHandler(onDragLeave)}
      onDrop={attachHandler(onDrop)}
      onDragEnd={attachHandler(onDragEnd)}
      data-dragging={isDragging ? 'true' : undefined}
      data-dragover={isDragOver ? 'true' : undefined}
    >
      <div className='reportsListItem__left'>
        <div className='reportsListItem__left--icon'>
          <CustomSVGs
            fillColor={iconColor ? iconColor : 'var(--black-white-color)'}
            svg={icon}
          />
        </div>

        <div className='reportsListItem__left--text'>
          <h3>{title}</h3>
          <span>{subTitle}</span>

          {shortCode ? (
            <div className='reportsListItem__left--text-shortcode'>
              <code className='shortcode-value'>{shortCode}</code>
            </div>
          ) : null}

          {tag ? (
            <div className='reportsListItem__left--text-tag'>
              <div>{tag}</div>
            </div>
          ) : null}
        </div>
      </div>

      <div className='reportsListItem__right'>
        {/* Download Button */}
        {showDownload && (
          <div className='reportsListItem__right--copy'>
            <div
              role='button'
              aria-label={t('Download') as string}
              onClick={() => {
                if (!downloadLoading) handleDownloadClick();
              }}
              onMouseEnter={() => handleMouseEnter(`${id}:download`)}
              onMouseLeave={handleMouseLeave}
              className='reportsListItem__right--copy-button'
              data-copied={downloadedId === id}
              style={{ cursor: downloadLoading ? 'not-allowed' : 'pointer' }}
            >
              {downloadLoading ? (
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #6B7280',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : downloadedId === id ? (
                <CustomSVGs svg={filledCheckboxSVG} />
              ) : (
                <CustomSVGs svg={download2SVG} />
              )}
              {hoverKey === `${id}:download` && (
                <div className='reportsListItem__right--copy-tooltip'>
                  {downloadLoading
                    ? t('Generating')
                    : downloadedId === id
                    ? t('Downloaded')
                    : t('Download')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Copy Button */}
        {showCopy && (
          <div className='reportsListItem__right--copy'>
            {showSyncStatus && (
              <div
                className={`reportsListItem__right--sync-label ${
                  syncStatusIsSynced
                    ? 'reportsListItem__right--sync-label--synced'
                    : 'reportsListItem__right--sync-label--not-synced'
                }`}
              >
                {syncStatusLabel}
              </div>
            )}
            <div
              role='button'
              aria-label={t('CopyShortcode') as string}
              onClick={() => shortCode && copyToClipboard(shortCode, id)}
              onMouseEnter={() => handleMouseEnter(`${id}:copy`)}
              onMouseLeave={handleMouseLeave}
              className='reportsListItem__right--copy-button'
              data-copied={copiedId === id}
            >
              {copiedId === id ? (
                <CustomSVGs svg={filledCheckboxSVG} />
              ) : (
                <CustomSVGs svg={copySVG} />
              )}
              {hoverKey === `${id}:copy` && (
                <div className='reportsListItem__right--copy-tooltip'>
                  {copiedId === id ? t('Copied') : t('CopyShortcode')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit Button */}
        {onEditClick && (
          <div
            onClick={() => {
              if (!editLoading) onEditClick();
            }}
            className='reportsListItem__right--config'
            style={{ cursor: editLoading ? 'not-allowed' : 'pointer' }}
          >
            {editLoading ? (
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #6B7280',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            ) : (
              <CustomSVGs
                fillColor={
                  editIconColor ? editIconColor : 'var(--black-white-color)'
                }
                svg={editIcon}
                className={editIconSize === '1.4rem' ? 'edit-icon-small' : ''}
              />
            )}
          </div>
        )}

        {/* Config / Delete Button */}
        <div
          onClick={() => {
            if (!configLoading) onConfigClick();
          }}
          className='reportsListItem__right--config'
          style={{ cursor: configLoading ? 'not-allowed' : 'pointer' }}
        >
          {configLoading ? (
            <div
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #FF4B4B',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          ) : (
            <CustomSVGs
              fillColor={
                configIconColor ? configIconColor : 'var(--black-white-color)'
              }
              svg={configIcon}
            />
          )}
        </div>

        {/* Drag Handle */}
        {dragIcon ? (
          <div
            className='reportsListItem__right--config drag-handle'
            draggable={!isDragDisabled && !dragLoading}
            onDragStart={(event) => {
              if (isDragDisabled || dragLoading) {
                event.preventDefault();
                return;
              }
              const previewElement = dragHandleRef.current?.closest(
                '.reportsListItem'
              ) as HTMLElement | null;
              if (previewElement) {
                const { width, height, left, top } =
                  previewElement.getBoundingClientRect();
                const pointerOffsetX = event.clientX - left;
                const pointerOffsetY = event.clientY - top;
                const clone = previewElement.cloneNode(true) as HTMLElement;
                clone.style.position = 'absolute';
                clone.style.top = '-9999px';
                clone.style.left = '-9999px';
                clone.style.width = `${width}px`;
                clone.style.height = `${height}px`;
                clone.style.pointerEvents = 'none';
                document.body.appendChild(clone);
                const offsetX = Number.isFinite(pointerOffsetX)
                  ? pointerOffsetX
                  : width - 12;
                const offsetY = Number.isFinite(pointerOffsetY)
                  ? pointerOffsetY
                  : height / 2;
                event.dataTransfer.setDragImage(clone, offsetX, offsetY);
                requestAnimationFrame(() => {
                  if (clone.parentNode) {
                    clone.parentNode.removeChild(clone);
                  }
                });
              }
              onDragStart?.(event);
            }}
            onDragEnd={attachHandler(onDragEnd)}
            onMouseEnter={() => handleMouseEnter(`${id}:drag`)}
            onMouseLeave={handleMouseLeave}
            style={{
              cursor: dragLoading || isDragDisabled ? 'not-allowed' : 'grab',
            }}
            aria-label='Reorder'
            ref={dragHandleRef}
            title={t('Reorder')}
          >
            {dragLoading ? (
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #5573f7',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            ) : (
              <CustomSVGs
                fillColor={
                  dragIconColor ? dragIconColor : 'var(--black-white-color)'
                }
                svg={dragIcon}
              />
            )}
            {hoverKey === `${id}:drag` && !dragLoading && (
              <div className='reportsListItem__right--copy-tooltip'>
                {t('Reorder')}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ReportsListItem;
